import { ConfirmationDialog } from '@/components/common'
import { setMTNavigationGuard } from '@/multi-territory/mt-utils/mt-navigation-guard'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

export const MT_MSG_UNSAVED_CHANGES =
  'You have unsaved changes. Navigating away will discard your edits. Do you want to proceed?'

/**
 * Unsaved-changes guard for MT inline-edit screens (Service Master, Payee
 * Master, Prepare, ...).
 *
 * While `active` (edit mode is on):
 * - `guardUnsavedChanges(action)` opens the confirmation dialog instead of
 *   running `action`; Proceed discards edits (`onDiscard`) then runs it.
 * - MT sidebar, country switcher and key-filter View / Entity / TIN changes are
 *   intercepted via the shared navigation guard registry.
 * - In-app `#/` links (route tabs, workflow steps) are intercepted in the
 *   capture phase so the route does not change until the user proceeds.
 * - Browser refresh / tab close shows the native "leave site?" prompt.
 *
 * Render `unsavedChangesDialog` once in the screen's JSX.
 */
export function useMTUnsavedChangesGuard(
  active: boolean,
  onDiscard: () => void
) {
  const navigate = useNavigate()
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)
  // Always call the latest discard handler (it closes over component state).
  const onDiscardRef = useRef(onDiscard)
  onDiscardRef.current = onDiscard

  const guardUnsavedChanges = useCallback(
    (action: () => void) => {
      if (!active) {
        action()
        return
      }
      setPendingAction(() => action)
    },
    [active]
  )

  const handleProceed = () => {
    const action = pendingAction
    setPendingAction(null)
    onDiscardRef.current()
    action?.()
  }
  const handleCancel = () => setPendingAction(null)

  useEffect(() => {
    if (!active) return

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }

    const unregister = setMTNavigationGuard(proceed => {
      setPendingAction(() => proceed)
      return true
    })

    const onLinkClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const anchor = (e.target as HTMLElement | null)?.closest?.('a')
      const href = anchor?.getAttribute('href')
      if (!anchor || !href || !href.startsWith('#/')) return
      if (anchor.target && anchor.target !== '_self') return
      const to = href.slice(1)
      if (to === window.location.hash.slice(1)) return
      e.preventDefault()
      e.stopPropagation()
      setPendingAction(() => () => navigate(to))
    }

    window.addEventListener('beforeunload', onBeforeUnload)
    document.addEventListener('click', onLinkClick, true)
    return () => {
      unregister()
      window.removeEventListener('beforeunload', onBeforeUnload)
      document.removeEventListener('click', onLinkClick, true)
    }
  }, [active, navigate])

  const unsavedChangesDialog = (
    <ConfirmationDialog
      open={pendingAction !== null}
      onClose={handleCancel}
      onConfirm={handleProceed}
      onCancel={handleCancel}
      title={MT_MSG_UNSAVED_CHANGES}
      message=" "
      confirmText="Proceed"
      type="warning"
      itemType=""
      showCancelButton={true}
    />
  )

  return { guardUnsavedChanges, unsavedChangesDialog }
}
